<template>
  <h5 class="text-center">Ler XML</h5>
  <div class="alert alert-primary border-0 alert-dismissible fade show">
    Para cadastrar uma medição, é necessário selecionar o arquivo XML da nota.
    Se não possuir um arquivo XML, clique em "Não tem XML" e forneça uma
    justificativa.
    <button
      type="button"
      class="btn-close"
      data-bs-dismiss="alert"
      title="Fechar"
    />
  </div>
  <div class="row g-3">
    <div class="col-12">
      <label class="form-label">Arquivo em XML:</label>
      <div class="input-group">
        <XmlInput @load="measure.xmlContent = $event" />
        <button
          type="button"
          class="btn btn-yellow ms-2"
          title="Não tem XML"
          @click="openJustifyWithoutXml"
        >
          Não tem XML
        </button>
      </div>
    </div>
    <div class="col-12 col-md-6">
      <label class="form-label">
        Data de Vencimento: <span class="text-danger">*</span>
      </label>
      <input type="date" class="form-control" v-model="measure.dueDate" />
    </div>
    <div class="col-12 col-md-6">
      <label class="form-label">
        Forma de Pgto/Recbto: <span class="text-danger">*</span>
      </label>
      <select class="form-select" v-model="measure.typeOfPayment">
        <option value="" disabled>Selecione uma opção</option>
        <option
          v-for="typeOfPayment in typesOfPayment"
          :value="typeOfPayment"
          :key="typeOfPayment.codigo"
        >
          {{ typeOfPayment.descricao }}
        </option>
      </select>
    </div>
    <div class="col-12">
      <label class="form-label">
        Arquivo(s) em PDF: Nota fiscal, boleto e outros.
        <span class="text-danger">*</span>
      </label>
      <div class="input-group">
        <input
          type="file"
          ref="newMeasureFile"
          class="form-control"
          multiple="multiple"
          accept="application/pdf"
          @change="addFile"
        />
      </div>
    </div>
    <div v-if="measure.fileNames.length > 0" class="col-12">
      <p v-for="(file, index) in measure.fileNames" :key="index" class="mb-0">
        <button
          type="button"
          class="btn btn-icon btn-link text-danger"
          title="Remover arquivo"
          @click="removeFile(index)"
        >
          <i class="ph-x" />
        </button>
        {{ file }}
      </p>
    </div>
    <div v-if="showJustifyWithoutXml" class="col-12">
      <label class="form-label">
        Justificativa: <span class="text-danger">*</span>
      </label>
      <textarea
        class="form-control"
        rows="3"
        v-model="measure.xmlJustify"
        placeholder="Informe sua justificativa..."
      />
      <div class="text-end mt-3">
        <button
          type="button"
          class="btn btn-secondary rounded-pill"
          title="Prosseguir sem XML"
          :disabled="!canContinue || disableButton"
          @click="finishWithOutXml"
        >
          Prosseguir sem XML
        </button>
      </div>
    </div>
  </div>
  <div v-if="measure.xmlContent">
    <hr />
    <h5 class="text-center">XML Lido</h5>
    <textarea
      class="form-control"
      rows="3"
      v-model="measure.xmlContent"
      disabled
    />
    <div v-if="measure.noteValue" class="mt-2">
      <p>
        Os valores da nota são
        <strong>{{ formatToCurrencyBr(measure.noteValue) }}</strong> como total
        e <strong>{{ formatToCurrencyBr(measure.noteValueNet) }}</strong> como
        líquido. Se estiverem corretos, clique em "Prosseguir".
      </p>
      <div class="text-end mt-3">
        <button
          type="button"
          class="btn btn-secondary rounded-pill"
          title="Prosseguir"
          :disabled="!canContinue"
          @click="finishWithXml"
        >
          Prosseguir
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import XmlInput from '@/components/documents/commons/XmlInput.vue'
import xml2js from 'xml2js'
import { formatToCurrencyBr, getValueNote } from '@/utils/currency'

export default {
  name: 'UploadXmlFile',

  props: {
    contractId: {
      type: String,
      required: false,
    },
    ategPcTecXmlNF: {
      type: Object,
      required: false,
    },
    ategPcTecPdfNF: {
      type: Object,
      required: false,
    },
  },

  components: {
    XmlInput,
  },

  emits: ['forwardXMLReader'],

  data() {
    return {
      typesOfPayment: [],
      showJustifyWithoutXml: false,
      disableButton: true,
      measure: {
        xmlContent: null,
        dueDate: '',
        typeOfPayment: '',
        noteValue: null,
        noteValueNet: null,
        xmlJustify: '',
        jsonContent: null,
        attach: [],
        fileNames: [],
      },
      files: null,
      formatToCurrencyBr,
    }
  },

  computed: {
    canContinue() {
      return this.measure.dueDate !== '' && this.measure.attach.length > 0
    },
  },

  watch: {
    'measure.xmlContent': {
      handler(newVal, oldVal) {
        if (newVal !== null) {
          this.showJustifyWithoutXml = false
          this.measure.xmlJustify = ''
          this.convertXml2Js()
        }
      },
    },

    'measure.xmlJustify': {
      handler(newVal, oldVal) {
        if (newVal && newVal.length > 0) {
          this.disableButton = false
        } else {
          this.disableButton = true
        }
      },
    },
  },

  methods: {
    async getTypesOfPayment() {
      try {
        this.typesOfPayment = await this.$store.dispatch('getTypesOfPayment')
      } catch (error) {
        if (error && error.message) {
          this.$root.$refs.notification.warning(error.message)
        } else {
          this.$root.$refs.notification.error(
            'Não foi possível se conectar ao servidor.',
          )
        }
      }
    },

    async convertXml2Js() {
      const parser = new xml2js.Parser()

      parser
        .parseStringPromise(this.measure.xmlContent)
        .then((result) => {
          this.measure.jsonContent = result
          const valueNote = getValueNote(result)
          if (valueNote) {
            this.measure.noteValue = valueNote.valorTotal
            this.measure.noteValueNet = valueNote.valorLiquido
          } else {
            this.$root.$refs.notification.error(
              'Houve um erro na leitura dos valores no XML. Por favor, entre em contato com o suporte e forneça o modelo do XML para que possamos adicionar este formato.',
            )
          }
        })
        .catch((err) => {
          console.log(err)
          this.$root.$refs.notification.error('Erro ao converter XML em JSON.')
        })
    },

    openJustifyWithoutXml() {
      this.showJustifyWithoutXml = true
      this.measure.xmlContent = null
      this.measure.noteValue = null
      this.measure.noteValueNet = null
      this.measure.jsonContent = null
    },

    addFile(event) {
      const { files } = event.target
      this.files = files

      // eslint-disable-next-line no-restricted-syntax
      for (const file of this.files) {
        this.measure.fileNames.push(file.name)

        const fileReader = new FileReader()
        fileReader.addEventListener('load', () => {
          this.measure.attach.push(fileReader.result)
        })

        fileReader.readAsDataURL(file)
      }

      this.files = []
      this.$refs.newMeasureFile.value = null
    },

    removeFile(index) {
      this.measure.attach.splice(index, 1)
      this.measure.fileNames.splice(index, 1)
    },

    finishWithOutXml() {
      if (this.measure.typeOfPayment === '') {
        this.$root.$refs.notification.warning(
          'Selecione uma forma de pagamento.',
        )
        return
      }
      this.$emit('forwardXMLReader', {
        dueDate: this.measure.dueDate,
        typeOfPayment: {
          code: this.measure.typeOfPayment.codigo,
          description: this.measure.typeOfPayment.descricao,
        },
        xml: null,
        noteValue: null,
        noteValueNet: null,
        xmlJustify: this.measure.xmlJustify,
        jsonFile: null,
        attach: this.measure.attach,
        fileNames: this.measure.fileNames,
      })
    },

    finishWithXml() {
      if (this.measure.typeOfPayment === '') {
        this.$root.$refs.notification.warning(
          'Selecione uma forma de pagamento.',
        )
        return
      }
      this.$emit('forwardXMLReader', {
        dueDate: this.measure.dueDate,
        typeOfPayment: {
          code: this.measure.typeOfPayment.codigo,
          description: this.measure.typeOfPayment.descricao,
        },
        xml: this.measure.xmlContent,
        noteValue: this.measure.noteValue,
        noteValueNet: this.measure.noteValueNet,
        xmlJustify: '',
        jsonFile: this.measure.jsonContent,
        attach: this.measure.attach,
        fileNames: this.measure.fileNames,
      })
    },
  },

  mounted() {
    this.getTypesOfPayment()

    if (this.ategPcTecXmlNF && this.ategPcTecXmlNF.file) {
      const binaryData = Buffer.from(this.ategPcTecXmlNF.file, 'base64')
      this.measure.xmlContent = binaryData.toString('utf-8')
    }

    if (this.ategPcTecPdfNF && this.ategPcTecPdfNF.file) {
      this.measure.attach.push(
        `data:application/pdf;base64,${this.ategPcTecPdfNF.file}`,
      )
      this.measure.fileNames.push(this.ategPcTecPdfNF.name)
    }
  },
}
</script>
